import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Trash2, Plus, DollarSign, TrendingDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";

const COMPANY_EXPENSES = [
  "Marketing Cost",
  "Payment Gateway",
  "Referral Sales Comission",
  "Office Expenses",
  "Employee Salary",
  "Technology & Software",
  "Legal & Compliance",
  "Miscellaneous",
];

const TRAVEL_EXPENSES = [
  "Train / Flight booking",
  "Hotel booking",
  "Transportation booking",
  "Gifts & Welcome kit",
  "Driver Allowances",
  "Meals",
  "Sightseeing & Entry Tickets",
  "Tour manager cost",
  "GST",
];

const INCOME_CATEGORIES = [
  "Package Revenue",
  "Traveler Payment",
  "Commission",
  "Other Income",
];

export default function Expenses() {
  const [expenses, setExpenses] = useState([]);
  const [packages, setPackages] = useState([]);
  const [travelers, setTravelers] = useState([]);
  const [formData, setFormData] = useState({
    entry_type: "company_expense",
    category: "",
    amount: "",
    note: "",
    source_type: "package",
    package_id: "",
    traveler_id: "",
  });
  const [summary, setSummary] = useState({ company_expense: 0, travel_expense: 0, income: 0, net_income: 0 });
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());

  useEffect(() => {
    fetchExpenses();
    loadReferences();
  }, []);

  const loadReferences = async () => {
    try {
      const [packageRes, travelerRes] = await Promise.all([api.get("/packages"), api.get("/travelers")]);
      setPackages(packageRes.data || []);
      setTravelers(travelerRes.data || []);
    } catch (err) {
      toast.error("Failed to load package or traveler data");
    }
  };

  const fetchExpenses = async () => {
    try {
      setLoading(true);
      const r = await api.get("/finance");
      setExpenses(r.data || []);
      calculateSummary(r.data || []);
    } catch (err) {
      toast.error("Failed to load expenses");
    } finally {
      setLoading(false);
    }
  };

  const calculateSummary = (data) => {
    const totals = { company_expense: 0, travel_expense: 0, income: 0, net_income: 0 };
    data.forEach((exp) => {
      if (exp.entry_type === "company_expense") {
        totals.company_expense += exp.amount || 0;
      } else if (exp.entry_type === "travel_expense") {
        totals.travel_expense += exp.amount || 0;
      } else if (exp.entry_type === "income") {
        totals.income += exp.amount || 0;
      }
    });
    totals.net_income = totals.income - totals.company_expense - totals.travel_expense;
    setSummary(totals);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.category || !formData.amount) {
      toast.error("Please fill in all required fields");
      return;
    }
    if (formData.entry_type === "income") {
      if (formData.source_type === "package" && !formData.package_id) {
        toast.error("Please select a package for income source");
        return;
      }
      if (formData.source_type === "traveler" && !formData.traveler_id) {
        toast.error("Please select a traveler for income source");
        return;
      }
    }
    if (isNaN(parseFloat(formData.amount)) || parseFloat(formData.amount) <= 0) {
      toast.error("Amount must be a positive number");
      return;
    }

    try {
      const payload = {
        entry_type: formData.entry_type,
        category: formData.category,
        amount: parseFloat(formData.amount),
        note: formData.note || null,
        source_type: formData.entry_type === "income" ? formData.source_type : null,
        package_id: formData.entry_type === "income" && formData.source_type === "package" ? formData.package_id : null,
        traveler_id: formData.entry_type === "income" && formData.source_type === "traveler" ? formData.traveler_id : null,
      };
      const r = await api.post("/finance", payload);
      setExpenses([r.data, ...expenses]);
      calculateSummary([r.data, ...expenses]);
      setFormData({
        entry_type: "company_expense",
        category: "",
        amount: "",
        note: "",
        source_type: "package",
        package_id: "",
        traveler_id: "",
      });
      toast.success("Expense added successfully");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add expense");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this expense?")) return;
    try {
      await api.delete(`/finance/${id}`);
      const updated = expenses.filter((e) => e.id !== id);
      setExpenses(updated);
      calculateSummary(updated);
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      toast.success("Expense deleted");
    } catch (err) {
      toast.error("Failed to delete expense");
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) {
      toast.error("No expenses selected");
      return;
    }
    if (!window.confirm(`Delete ${selectedIds.size} selected expense(s)?`)) return;

    try {
      let deletedCount = 0;
      for (const id of selectedIds) {
        try {
          await api.delete(`/finance/${id}`);
          deletedCount++;
        } catch (err) {
          console.error(`Failed to delete ${id}`);
        }
      }
      const updated = expenses.filter((e) => !selectedIds.has(e.id));
      setExpenses(updated);
      calculateSummary(updated);
      setSelectedIds(new Set());
      toast.success(`Deleted ${deletedCount} expense(s)`);
    } catch (err) {
      toast.error("Failed to delete expenses");
    }
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === expenses.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(expenses.map((e) => e.id)));
    }
  };

  const toggleSelect = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    setSelectedIds(next);
  };

  const categories = formData.entry_type === "company_expense"
    ? COMPANY_EXPENSES
    : formData.entry_type === "travel_expense"
    ? TRAVEL_EXPENSES
    : INCOME_CATEGORIES;

  const getSourceLabel = (exp) => {
    if (exp.entry_type !== "income" || !exp.source_type) return "—";
    if (exp.source_type === "package") {
      const pkg = packages.find((p) => p.id === exp.package_id);
      return pkg ? `Package: ${pkg.title}` : `Package (${exp.package_id})`;
    }
    if (exp.source_type === "traveler") {
      const traveler = travelers.find((t) => t.id === exp.traveler_id);
      return traveler ? `Traveler: ${traveler.full_name}` : `Traveler (${exp.traveler_id})`;
    }
    return "—";
  };

  return (
    <div className="space-y-8" data-testid="expenses-page">
      {/* Header */}
      <div>
        <div className="overline">Financial Management</div>
        <h1 className="font-display text-4xl mt-2 text-[#0F172A]">Expenses</h1>
        <p className="text-[#64748B] mt-2">Track and manage company and travel expenses.</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Card className="p-6">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-sm font-medium text-[#64748B] mb-1">Company Expenses</div>
              <div className="font-display text-3xl text-[#0F172A]">₹{summary.company_expense.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</div>
            </div>
            <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-[#DC26261a] text-[#DC2626]">
              <TrendingDown size={20} />
            </div>
          </div>
        </Card>
        <Card className="p-6">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-sm font-medium text-[#64748B] mb-1">Travel Expenses</div>
              <div className="font-display text-3xl text-[#0F172A]">₹{summary.travel_expense.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</div>
            </div>
            <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-[#F59E0B1a] text-[#F59E0B]">
              <DollarSign size={20} />
            </div>
          </div>
        </Card>
      </div>

      {/* Form Section */}
      <Card className="p-6">
        <h2 className="font-display text-2xl text-[#0F172A] mb-6">Add New Expense</h2>
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Expense Type */}
            <div>
              <label className="block text-sm font-medium text-[#0F172A] mb-2">Expense Type</label>
              <Select
                value={formData.entry_type}
                onValueChange={(value) => setFormData({
                  ...formData,
                  entry_type: value,
                  category: "",
                  source_type: value === "income" ? formData.source_type : "package",
                  package_id: "",
                  traveler_id: "",
                })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select entry type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="company_expense">Company Expense</SelectItem>
                  <SelectItem value="travel_expense">Travel Expense</SelectItem>
                  <SelectItem value="income">Income</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-[#0F172A] mb-2">Category *</label>
              <Select
                value={formData.category}
                onValueChange={(value) => setFormData({ ...formData, category: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat} value={cat}>
                      {cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {formData.entry_type === "income" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-medium text-[#0F172A] mb-2">Income Source</label>
                <Select
                  value={formData.source_type}
                  onValueChange={(value) => setFormData({
                    ...formData,
                    source_type: value,
                    package_id: "",
                    traveler_id: "",
                  })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select source type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="package">Package</SelectItem>
                    <SelectItem value="traveler">Traveler</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {formData.source_type === "package" ? (
                <div>
                  <label className="block text-sm font-medium text-[#0F172A] mb-2">Package</label>
                  <Select
                    value={formData.package_id}
                    onValueChange={(value) => setFormData({ ...formData, package_id: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select package" />
                    </SelectTrigger>
                    <SelectContent>
                      {packages.map((pkg) => (
                        <SelectItem key={pkg.id} value={pkg.id}>
                          {pkg.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-[#0F172A] mb-2">Traveler</label>
                  <Select
                    value={formData.traveler_id}
                    onValueChange={(value) => setFormData({ ...formData, traveler_id: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select traveler" />
                    </SelectTrigger>
                    <SelectContent>
                      {travelers.map((traveler) => (
                        <SelectItem key={traveler.id} value={traveler.id}>
                          {traveler.full_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Amount */}
            <div>
              <label className="block text-sm font-medium text-[#0F172A] mb-2">Amount (₹) *</label>
              <Input
                type="number"
                placeholder="0.00"
                step="0.01"
                min="0"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              />
            </div>

            {/* Note */}
            <div>
              <label className="block text-sm font-medium text-[#0F172A] mb-2">Note</label>
              <Input
                type="text"
                placeholder="Optional note..."
                value={formData.note}
                onChange={(e) => setFormData({ ...formData, note: e.target.value })}
              />
            </div>
          </div>

          <Button type="submit" className="w-full md:w-auto">
            <Plus size={18} className="mr-2" />
            Add Expense
          </Button>
        </form>
      </Card>

      {/* Expenses List */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display text-2xl text-[#0F172A]">Expense History</h2>
          {selectedIds.size > 0 && (
            <Button
              onClick={handleBulkDelete}
              className="bg-[#DC2626] hover:bg-[#991B1B] text-white"
            >
              <Trash2 size={18} className="mr-2" />
              Delete {selectedIds.size} selected
            </Button>
          )}
        </div>
        {loading ? (
          <div className="text-center py-10 text-[#64748B]">Loading...</div>
        ) : expenses.length === 0 ? (
          <div className="text-center py-10 text-[#64748B]">No expenses yet. Add one to get started.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#E2E8F0]">
                  <th className="text-left py-3 px-4 w-10">
                    <input
                      type="checkbox"
                      checked={selectedIds.size === expenses.length && expenses.length > 0}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 cursor-pointer"
                    />
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-[#64748B]">Date</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-[#64748B]">Type</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-[#64748B]">Category</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-[#64748B]">Source</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-[#64748B]">Amount</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-[#64748B]">Note</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-[#64748B]">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E2E8F0]">
                {expenses.map((exp) => (
                  <tr
                    key={exp.id}
                    className={`hover:bg-[#F4F7FB] transition ${
                      selectedIds.has(exp.id) ? "bg-[#1E3A8A]/5" : ""
                    }`}
                  >
                    <td className="py-3 px-4">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(exp.id)}
                        onChange={() => toggleSelect(exp.id)}
                        className="w-4 h-4 cursor-pointer"
                      />
                    </td>
                    <td className="py-3 px-4 text-sm text-[#0F172A]">
                      {new Date(exp.created_at).toLocaleDateString("en-IN")}
                    </td>
                    <td className="py-3 px-4 text-sm">
                      <span
                        className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium ${
                          exp.entry_type === "company_expense"
                            ? "bg-[#DC2626]/10 text-[#DC2626]"
                            : "bg-[#F59E0B]/10 text-[#F59E0B]"
                        }`}
                      >
                        {exp.entry_type === "company_expense"
                          ? "Company"
                          : exp.entry_type === "travel_expense"
                          ? "Travel"
                          : "Income"}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm text-[#0F172A]">{exp.category}</td>
                    <td className="py-3 px-4 text-sm text-[#64748B]">{getSourceLabel(exp)}</td>
                    <td className="py-3 px-4 text-sm font-medium text-[#0F172A]">
                      ₹{parseFloat(exp.amount).toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                    </td>
                    <td className="py-3 px-4 text-sm text-[#64748B]">{exp.note || "—"}</td>
                    <td className="py-3 px-4 text-center">
                      <button
                        onClick={() => handleDelete(exp.id)}
                        className="inline-flex items-center justify-center w-8 h-8 rounded hover:bg-[#DC2626]/10 text-[#DC2626] transition"
                        title="Delete expense"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
